import { AUTH_ERROR_CODES, RETRYABLE_ERROR_CODES, ERROR_CODES, ErrorCode } from '../api/error_codes';
import { ApiResponse, isErrorResponse } from '../api/types';

export class ErrorHandler {
  // Check if error code is retryable
  static isRetryable(code: string): boolean {
    return RETRYABLE_ERROR_CODES.includes(code as ErrorCode);
  }

  // Check if error code is an authentication error
  static isAuthError(code: string): boolean {
    return AUTH_ERROR_CODES.includes(code as ErrorCode);
  }

  // Get user-friendly error message
  static getUserMessage(code: string, msg: string): string {
    // Map error codes to user-friendly messages
    const errorMessages: Record<string, string> = {
      [ERROR_CODES.USER_NOT_FOUND]: 'User account not found. Please check your credentials.',
      [ERROR_CODES.ACCOUNT_FAILED]: 'Invalid credentials. Please try again.',
      [ERROR_CODES.ACCOUNT_BLOCKED]: 'Your account has been blocked due to multiple failed login attempts.',
      [ERROR_CODES.SESSION_EXPIRED]: 'Your session has expired. Please log in again.',
      [ERROR_CODES.INVALID_TOKEN]: 'Invalid authentication. Please log in again.',
      [ERROR_CODES.NETWORK_ERROR]: 'Unable to connect to the server. Please check your connection.',
      [ERROR_CODES.TIMEOUT]: 'Request timed out. Please try again.',
      [ERROR_CODES.SERVICE_ERROR]: 'Service temporarily unavailable. Please try again later.',
      [ERROR_CODES.DEVICE_LIMIT_REACHED]: 'Maximum number of devices reached for your account.',
      [ERROR_CODES.FILE_UPLOAD_FAILED]: 'Failed to upload file. Please try again.',
      [ERROR_CODES.INVALID_INPUT]: 'Invalid input provided. Please check your data.',
      [ERROR_CODES.INCOMPLETE_PARAM]: 'Missing required information. Please fill in all fields.',
    };

    // Return custom message if available, otherwise use the server message
    return errorMessages[code] || msg || 'An unexpected error occurred. Please try again.';
  }

  // Retry a request with exponential backoff
  static async retryRequest<T>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    initialDelay: number = 1000
  ): Promise<T> {
    let lastError: any;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error: any) {
        lastError = error;
        
        // Check if error is retryable
        if (error.response?.data?.Code && !this.isRetryable(error.response.data.Code)) {
          throw error;
        }
        
        // Don't retry on the last attempt
        if (attempt === maxRetries - 1) {
          throw error;
        }
        
        // Exponential backoff delay
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  }

  // Handle axios error and convert to structured error
  static handleAxiosError(error: any): string {
    // Network errors (no response from server)
    if (!error.response) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return ERROR_CODES.NETWORK_ERROR;
      }
      if (error.code === 'ECONNREFUSED') {
        return ERROR_CODES.CONNECTION_REFUSED;
      }
      if (error.code === 'ETIMEDOUT') {
        return ERROR_CODES.TIMEOUT;
      }
      return ERROR_CODES.NETWORK_ERROR;
    }
    
    // Server responded with error
    if (error.response.data?.Code) {
      return error.response.data.Code;
    }
    
    // Fallback to generic error
    return ERROR_CODES.SERVICE_ERROR;
  }

  // Process API response and handle errors
  static processResponse<T>(response: ApiResponse<T>): T {
    if (isErrorResponse(response)) {
      const errorCode = response.Code;
      const errorMessage = this.getUserMessage(errorCode, response.Msg as string);
      
      const error = new Error(errorMessage);
      (error as any).code = errorCode;
      (error as any).originalMessage = response.Msg;
      
      throw error;
    }
    
    return response.Msg as T;
  }
}

// Helper function for handling API errors in action files
export function handleApiError(error: any): Error {
  // If error has a response property with data, extract error details
  if (error.response?.data) {
    console.error('API Error:', error.response.data);
    const { Code, Msg } = error.response.data;
    const userMessage = ErrorHandler.getUserMessage(Code || ERROR_CODES.SERVICE_ERROR, Msg);
    const enhancedError = new Error(userMessage);
    (enhancedError as any).code = Code;
    (enhancedError as any).originalMessage = Msg;
    return enhancedError;
  }
  
  // If error already has a code property, it's been processed
  if (error.code) {
    return error;
  }
  
  // For other errors, return as is
  return error;
}

// Wrapper function for API calls with retry logic
export async function apiCallWithRetry<T>(
  apiCall: () => Promise<T>,
  options?: {
    maxRetries?: number;
    initialDelay?: number;
    skipRetry?: boolean;
  }
): Promise<T> {
  const { maxRetries = 3, initialDelay = 1000, skipRetry = false } = options || {};
  
  // If retry is disabled, just make the call
  if (skipRetry) {
    return apiCall();
  }
  
  // Use the retry logic from ErrorHandler
  return ErrorHandler.retryRequest(apiCall, maxRetries, initialDelay);
}